import React, { useEffect, useMemo, useState } from 'react';
import { Steps, Spin, Card, Space, Typography } from 'antd';
import { UserOutlined, PhoneOutlined, HomeOutlined, MedicineBoxOutlined, FileTextOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getNurseProfile } from '../../api';
import PersonalInfoStep from './onboarding/PersonalInfoStep';
import ContactInfoStep from './onboarding/ContactInfoStep';
import AddressInfoStep from './onboarding/AddressInfoStep';
import MedicalUnitsStep from './onboarding/MedicalUnitsStep';
import DocumentsStep from './onboarding/DocumentsStep';

const { Step } = Steps;


const NurseOnboarding = () => {
  const [current, setCurrent] = useState(0);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getNurseProfile();
        const data = res.data || {};
        setProfile(data);
        // Determine first incomplete step
        const isPersonalDone = !!(data.firstName && data.lastName && data.dob);
        const isContactDone = !!data.phone;
        const isAddressDone = !!(data.address && data.address.street && data.address.city && data.address.state && data.address.zipCode && data.address.country);
        const isUnitsDone = Array.isArray(data.units) && data.units.length > 0;
        let idx = 0;
        if (!isPersonalDone) idx = 0;
        else if (!isContactDone) idx = 1;
        else if (!isAddressDone) idx = 2;
        else if (!isUnitsDone) idx = 3;
        else idx = 4;
        setCurrent(idx);
      } catch (e) {
        // If profile fetch fails, start at step 0
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const steps = useMemo(() => ([
    {
      title: 'Personal',
      icon: <UserOutlined />,
      content: (
        <PersonalInfoStep
          initial={profile}
          onNext={() => setCurrent((i) => i + 1)}
          onBack={null}
          onSaved={(patch) => setProfile((prev) => ({ ...(prev || {}), ...(patch || {}) }))}
        />
      ),
    },
    {
      title: 'Contact',
      icon: <PhoneOutlined />,
      content: (
        <ContactInfoStep
          initial={profile}
          onNext={() => setCurrent((i) => i + 1)}
          onBack={() => setCurrent((i) => Math.max(0, i - 1))}
          onSaved={(patch) => setProfile((prev) => ({ ...(prev || {}), ...(patch || {}) }))}
        />
      ),
    },
    {
      title: 'Address',
      icon: <HomeOutlined />,
      content: (
        <AddressInfoStep
          initial={profile}
          onNext={() => setCurrent((i) => i + 1)}
          onBack={() => setCurrent((i) => Math.max(0, i - 1))}
          onSaved={(patch) => setProfile((prev) => ({ ...(prev || {}), ...(patch || {}) }))}
        />
      ),
    },
    {
      title: 'Units',
      icon: <MedicineBoxOutlined />,
      content: (
        <MedicalUnitsStep
          initial={profile}
          onNext={() => setCurrent((i) => i + 1)}
          onBack={() => setCurrent((i) => Math.max(0, i - 1))}
          onSaved={(patch) => setProfile((prev) => ({ ...(prev || {}), ...(patch || {}) }))}
        />
      ),
    },
    {
      title: 'Documents',
      icon: <FileTextOutlined />,
      content: <DocumentsStep onFinish={() => navigate('/employee')} onBack={() => setCurrent((i) => Math.max(0, i - 1))} />,
    }
  ]), [profile]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      padding: 24,
   
    }}>
      <Card bordered style={{ width: '100%', maxWidth: 720, margin: '0 auto', height: '100%', borderRadius: 20, boxShadow: '0 12px 30px rgba(0,77,84,0.16), 0 6px 14px rgba(0,77,84,0.12)' }} bodyStyle={{ padding: 24 }}>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Typography.Title level={4} style={{ margin: 0 }}>Complete your profile</Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Follow the steps below to get your account ready.
          </Typography.Paragraph>
        </Space>
        <style>{`
          .onboarding-steps .ant-steps-item-process .ant-steps-item-icon {
            background-color: #004d54 !important;
            border-color: #004d54 !important;
          }
          .onboarding-steps .ant-steps-item-process .ant-steps-item-icon .ant-steps-icon {
            color: #ffffff !important;
          }
          .onboarding-steps .ant-steps-item-finish .ant-steps-item-icon {
            background-color: #004d54 !important;
            border-color: #004d54 !important;
          }
          .onboarding-steps .ant-steps-item-finish .ant-steps-item-icon .ant-steps-icon {
            color: #ffffff !important;
          }
          .onboarding-steps .ant-steps-item-finish .ant-steps-item-title::after {
            background-color: #004d54 !important;
          }
          .onboarding-steps .ant-steps-item-wait .ant-steps-item-icon {
            border-color: #004d54 !important;
          }
          .onboarding-steps .ant-steps-item-icon {
            width: 36px !important;
            height: 36px !important;
            border-radius: 8px !important;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
          .onboarding-steps .ant-steps-item-icon .ant-steps-icon {
            line-height: 1 !important;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
        `}</style>
        <Steps current={current} className="onboarding-steps" style={{ margin: '16px 0 24px' }}>
          {steps.map((s) => (
            <Step key={s.title} title={s.title} icon={s.icon} />
          ))}
        </Steps>
        <div>
          {steps[current].content}
        </div>
      </Card>
    </div>
  );
};

export default NurseOnboarding;


